import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { jobUrlSegment } from '../../lib/slug';

type OverviewRow = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  kolAcceptedAt: string | null;
  status: string;
  template?: { name: string } | null;
  job: {
    id: string;
    title: string;
    urlSlug?: string | null;
    brandOrg?: { name: string } | null;
    managedByOrg?: { name: string } | null;
  };
  invitation?: { id: string; kolProfile?: { name: string } | null } | null;
};

type ContractFilter = 'ALL' | 'ACKNOWLEDGED' | 'SENT' | 'DRAFT';

const CONTRACT_FILTERS: Array<{ value: ContractFilter; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'SENT', label: 'Sent' },
  { value: 'DRAFT', label: 'Draft' },
];

function contractPhase(row: OverviewRow): Exclude<ContractFilter, 'ALL'> {
  if (row.kolAcceptedAt) return 'ACKNOWLEDGED';
  if (row.invitation) return 'SENT';
  return 'DRAFT';
}

function contractStatusClasses(row: OverviewRow) {
  if (row.kolAcceptedAt) {
    return 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300 border-emerald-500/30';
  }
  if (row.invitation) {
    return 'bg-amber-400/15 text-amber-500 dark:text-amber-300 border-amber-400/30';
  }
  return 'bg-glass-3 text-glass-text-2 border-glass';
}

export default function ContractsOverviewPage({ role }: { role: 'AGENCY' | 'BRAND' | 'ADMIN' }) {
  const [rows, setRows] = useState<OverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilter, setMobileFilter] = useState<ContractFilter>('ALL');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/jobs/contracts-overview');
        setRows(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const jobHref = (job: OverviewRow['job']) => {
    const seg = jobUrlSegment(job);
    if (role === 'AGENCY') return `/agency/campaigns/${seg}?tab=contracts`;
    if (role === 'BRAND') return `/brand/jobs/${seg}?tab=contracts`;
    return '/admin/campaigns';
  };

  const mobileRows = mobileFilter === 'ALL' ? rows : rows.filter((row) => contractPhase(row) === mobileFilter);
  const filterCounts = rows.reduce<Record<Exclude<ContractFilter, 'ALL'>, number>>(
    (counts, row) => {
      counts[contractPhase(row)] += 1;
      return counts;
    },
    { ACKNOWLEDGED: 0, SENT: 0, DRAFT: 0 }
  );

  return (
    <div className="ph-no-capture space-y-6 max-w-6xl">
      <div>
        <h1 className="text-[1.375rem] font-semibold tracking-tightest text-glass-text md:text-[2.25rem]">Contracts overview</h1>
        <p className="mt-1 text-xs text-glass-text-2 md:text-sm">
          All generated contracts across your jobs. Status reflects whether the draft is job-wide, sent to a creator (invitation-linked), or acknowledged in-app.
        </p>
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CONTRACT_FILTERS.map((filter) => {
          const active = mobileFilter === filter.value;
          const count = filter.value === 'ALL' ? rows.length : filterCounts[filter.value];
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => setMobileFilter(filter.value)}
              className={`min-h-11 shrink-0 cursor-pointer rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                active
                  ? 'border-accent2/40 bg-accent2/15 font-semibold text-accentText'
                  : 'border-glass bg-glass-2 font-medium text-glass-text-2 hover:bg-glass-3'
              }`}
              aria-pressed={active}
            >
              {filter.label} {count}
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader className="hidden md:flex">
          <CardTitle>Recent contracts</CardTitle>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          {loading ? (
            <p className="px-[13px] py-10 text-center text-sm text-glass-text-3 md:px-0 md:py-0 md:text-left">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-[13px] py-10 text-center text-sm text-glass-text-3 md:px-0 md:py-0 md:text-left">No contracts yet.</p>
          ) : (
            <>
              <div className="divide-y divide-glass md:hidden">
                {mobileRows.map((row) => (
                  <div key={row.id} className="flex min-h-[88px] items-center gap-2 px-[13px] py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold text-glass-text">{row.job.title}</span>
                        <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${contractStatusClasses(row)}`}>
                          {row.status}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-xs text-glass-text-2">
                        {row.job.brandOrg?.name || row.job.managedByOrg?.name || 'No brand'}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-glass-text-3">
                        {row.template?.name || 'No template'} · {row.invitation?.kolProfile?.name || 'Job-wide'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-glass-text-3">
                        Updated {new Date(row.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    <Link
                      to={jobHref(row.job)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-glass bg-glass-2 text-glass-text-2 transition-colors hover:border-accent2/40 hover:bg-glass-3 hover:text-accent2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
                      aria-label={`Open ${row.job.title}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                ))}
                {mobileRows.length === 0 && (
                  <p className="px-[13px] py-10 text-center text-sm text-glass-text-3">
                    No contracts match this status.
                  </p>
                )}
              </div>

              <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-glass text-left text-glass-text-3">
                    <th className="pb-2 pr-4">Campaign</th>
                    <th className="pb-2 pr-4">Template</th>
                    <th className="pb-2 pr-4">Creator</th>
                    <th className="pb-2 pr-4">Status</th>
                    <th className="pb-2 pr-4">Updated</th>
                    <th className="pb-2">Open</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-glass">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-glass-2">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-glass-text">{r.job.title}</div>
                        <div className="text-xs text-glass-text-3">{r.job.brandOrg?.name}</div>
                      </td>
                      <td className="py-3 pr-4 text-glass-text-2">{r.template?.name || '—'}</td>
                      <td className="py-3 pr-4 text-glass-text-2">{r.invitation?.kolProfile?.name || (r.invitation ? '—' : '—')}</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${contractStatusClasses(r)}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-glass-text-2 whitespace-nowrap">
                        {new Date(r.updatedAt).toLocaleString()}
                      </td>
                      <td className="py-3">
                        <Link to={jobHref(r.job)} className="text-accent2 hover:underline">
                          {role === 'ADMIN' ? 'Campaigns' : 'Job'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
